import { inject, Injectable } from '@angular/core';
import { EntityState } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { map, Observable, of, take } from 'rxjs';

import { CanvasElement, CanvasElementId, PropsOf } from '@blaze-runtime/engine-domain';
import {
  CanvasElementPropertiesWithData,
  FormContainerModelField,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { ColumnQuery, DatasetColumn, matchColumn } from '@blaze-shared/schema-domain';

import { getElementsEntityState } from '../../../+state/elements/elements.selectors';
import {
  isFormList,
  isVariantOfFormContainer,
} from '../../../canvas-elements/element-model/element-type-guards';
import {
  checkboxGroupOption,
  datetimeOption,
  dropdownOption,
  formFieldOptions,
  GeneratorInputOption,
  getFormFieldDataTypeMapping,
  radioOption,
} from '../../../canvas-elements/element-model/form-fields-generation';
import {
  FORM_BINDING_KEY,
  FormElement,
} from '../../../elements/form-container/form-container.model';
import { findWrapperParentId } from '../../../utils/elements/element.utils';

/**
 * Answers what the editor needs to change a form field's control: which form a field
 * belongs to, and which control types that field may be rendered as.
 */
@Injectable({ providedIn: 'root' })
export class FormFieldControlsService {
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);

  /**
   * Finds the formlike container an element is a field of.
   * @param elementsState the elements to search; read from the store when not given
   */
  findOwningForm(
    elementId: CanvasElementId,
    elementsState?: EntityState<CanvasElement>,
  ): Observable<FormElement | undefined> {
    const entities$ = elementsState
      ? of(elementsState)
      : this.store.select(getElementsEntityState).pipe(take(1));

    return entities$.pipe(
      map((elements) => {
        const owningFormId = findWrapperParentId(
          elements,
          elementId,
          (element) => isVariantOfFormContainer(element) || isFormList(element),
        );
        // why: a details view and an editable list own fields the same way a form does, and
        // carry the props read here; ticket 43 gives the family one type to say that
        return owningFormId ? (elements.entities[owningFormId] as FormElement) : undefined;
      }),
    );
  }

  /**
   * Lists the control types each of a form's fields may be rendered as, keyed by column
   * name — the choice the properties editor offers for the field.
   */
  // TODO(michal): check whether type is URL and filter it out if it's not
  getControlOptionsByField(
    formProps: PropsOf<'formContainer'> & CanvasElementPropertiesWithData,
  ): Record<string, GeneratorInputOption[]> {
    const model = formProps.content.model;
    if (!model?.length) {
      return {};
    }

    const isFormReadonly = formProps?.content?.action === 'view';
    const formBinding = formProps?.data?.find((data) => data.key === FORM_BINDING_KEY);

    return model.reduce(
      (optionsByField, field) => {
        const columnName = this.schemaService.getColumnName(field.fieldId || field.field);
        if (!columnName) {
          console.warn(`Could not resolve column name for form field item`, field);
          return optionsByField;
        }

        const fieldQuery: ColumnQuery = { id: field.fieldId, name: field.field };
        const column = formBinding?.dataSource?.columns.find((candidate) =>
          matchColumn(candidate, fieldQuery),
        );
        optionsByField[columnName] = listControlOptions(field, column, isFormReadonly);
        return optionsByField;
      },
      {} as Record<string, GeneratorInputOption[]>,
    );
  }
}

/** The control types offerable for one field, given the column it is bound to. */
function listControlOptions(
  field: FormContainerModelField,
  column: DatasetColumn | undefined,
  isFormReadonly: boolean,
): GeneratorInputOption[] {
  if (!field.dataType) {
    return [];
  }

  let options = getFormFieldDataTypeMapping(
    isFormReadonly || (column?.isInline ?? false),
    field.dataType,
  );

  const isMultiLinkField = (column?.multipleValues ?? column?.link?.multipleValues) || false;
  options = isMultiLinkField
    ? // a multi link cannot be picked with a single-choice control
      options.filter(
        (option) => option.value !== dropdownOption.value && option.value !== radioOption.value,
      )
    : options.filter((option) => option.value !== checkboxGroupOption.value);

  if (field.dataType === 'Date' || field.dataType === 'Time') {
    options = options.filter((option) => option.value !== datetimeOption.value);
  }

  if (column?.format?.formatType === 'TimeZone') {
    options = options.filter(
      (option) => option.value === 'dropdown' || option.value === 'textField',
    );
  }

  // For non-URL strings, don't suggest PDF Viewer specified in @see form-fields-generation.utils.ts
  if (column?.dataType === 'String' && column?.format?.formatType !== 'Url') {
    options = options.filter((option) => option.value !== 'pdfViewer');
  }

  // If current selection is outside of the option list - prepend the current selection
  if (field.columnType && !options.some((option) => option.value === field.controlType)) {
    const currentOption = formFieldOptions.find((option) => option.value === field.controlType);
    if (currentOption) {
      options = [currentOption, ...options];
    }
  }

  return options;
}
